'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { MessageCircle, X } from 'lucide-react';
import { useChatUI } from '@/stores/chat-ui';
import { useCompareStore } from '@/stores/compare';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

/**
 * Small floating "Fråga Erik" pill bottom-right. Visible only when:
 *  - the chat panel is closed, AND
 *  - the customer hasn't dismissed it with the X.
 *
 * Once dismissed it stays gone (persisted) — the customer can still open the
 * chat from the mobile menu or the footer, which clears the dismissal and
 * brings the bubble back next session.
 *
 * Dold på produktsidor, i kassan och på sökresultatsidan — överallt där sidan
 * redan har ett eget flytande element i nederkanten som bubblan lade sig över.
 */
export function AskErikBubble() {
  const isOpen = useChatUI((s) => s.isOpen);
  const bubbleHidden = useChatUI((s) => s.bubbleHidden);
  const open = useChatUI((s) => s.open);
  const hideBubble = useChatUI((s) => s.hideBubble);
  const compareCount = useCompareStore((s) => s.items.length);
  const { locale } = useLocale();
  const pathname = usePathname() || '';

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Product pages have their own StickyBuyBar at the bottom — Erik would stack on
  // top of the buy CTA, so hide the bubble there (still reachable from the menu).
  const onProductPage = pathname.startsWith('/produkt/');
  // Kassan av samma skäl, plus ett tyngre: bubblan är fixerad ovanför betalraden
  // men ligger ändå ÖVER formuläret medan kunden skrollar. Uppmätt 2026-09-07 på
  // 390 px täckte den telefonfältet (y=700 mot bubblans y=704) och raden
  // "Beställ för 326 kr till för att sänka frakten" — alltså både ett fält kunden
  // ska fylla i och en rad som höjer ordervärdet. Sju av tio kassabesök sedan
  // 4 september slutade utan ett klick på Betala.
  const onCheckout = pathname.startsWith('/kassa');
  // Sökresultatsidan har sin egen flytande Filter-knapp i samma hörn. Uppmätt
  // 2026-09-08 på 390 px: Filter låg på x 146-244 och bubblan på x 223-378, båda
  // y 732-772 — bubblan (z40) kapade ordet "Filter" (z30). På en sida med 56 596
  // träffar är filtret det viktigaste verktyget som finns.
  const onSearch = pathname.startsWith('/sok');
  // Mirror MobileBottomNav's hide list: the bottom nav is present everywhere except these.
  const hasBottomNav = !(onProductPage || pathname.startsWith('/kassa') || pathname.startsWith('/foretag/login') || pathname.startsWith('/login'));

  // Yield to the compare bar when something is being compared — they'd otherwise
  // stack on top of each other in the same corner.
  if (!mounted || isOpen || bubbleHidden || compareCount > 0 || onProductPage || onCheckout || onSearch) return null;

  // Sit just above the bottom nav when it's present (accounting for the iOS safe
  // area so it never overlaps the nav), otherwise drop to a normal low FAB so it
  // doesn't float awkwardly high on pages without a bottom bar.
  const mobileBottom = hasBottomNav
    ? 'bottom-[calc(4.5rem_+_env(safe-area-inset-bottom))]'
    : 'bottom-[calc(1rem_+_env(safe-area-inset-bottom))]';

  return (
    <div
      className={`fixed z-40 ${mobileBottom} right-3 sm:bottom-6 sm:right-6 pointer-events-none`}
      // Lyft bubblan över en ev. fast bottenrad (kassans betalrad sätter --af-bottom-bar).
      // marginBottom används i stället för bottom så att klasserna ovan lämnas orörda —
      // annars la sig bubblan ovanpå betalknappen på mobil (uppmätt 2026-09-02).
      style={{ marginBottom: 'var(--af-bottom-bar, 0px)' }}
      aria-hidden={false}
    >
      <div className="pointer-events-auto inline-flex items-stretch rounded-full shadow-lg bg-[#0055A4] text-white overflow-hidden">
        <button
          type="button"
          onClick={open}
          className="flex items-center gap-2 pl-4 pr-3 py-2.5 hover:bg-[#0f2e5a] transition-colors text-sm font-medium"
        >
          <MessageCircle className="h-4 w-4" />
          {t(locale, 'chat.askErik')}
        </button>
        <button
          type="button"
          onClick={hideBubble}
          aria-label={locale === 'sv' ? 'Stäng — hittas i menyn' : 'Dismiss — available in the menu'}
          className="px-2 hover:bg-[#0f2e5a] border-l border-white/15 transition-colors flex items-center"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
